import { Dialog as MuiDialog, useMediaQuery, useTheme, DialogContent as MuiDialogContent } from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Stack, Typography } from '@mui/material';
import AppScrollbar from '@crema/components/AppScrollbar';

interface DialogContentProps extends React.PropsWithChildren {
  dividers: boolean;
  fullHeight: boolean;
  contentHeight?: string;
}

const DialogContent = ({ dividers, fullHeight, contentHeight, children }: DialogContentProps) => {
  return (
    <MuiDialogContent dividers={dividers}>
      <AppScrollbar
        sx={{
          height: fullHeight ? '70vh' : '100%',
          minHeight: contentHeight,
        }}
      >
        {children}
      </AppScrollbar>
    </MuiDialogContent>
  );
};

type DialogTitleProps = {
  title: any; //string OR Component
  subtitle?: any; //string OR Component
  onClose?: () => void;
  notUseCloseIcon?: boolean | undefined;
};

/**
 * Pod 프로젝트에서 사용하는 Dialog의 Title 공통 컴포넌트.
 * @param title
 * @param subtitle
 * @param onClose
 * @constructor
 * @author codestove
 */
const DialogTitle = ({ title, subtitle, onClose, notUseCloseIcon }: DialogTitleProps) => (
  <MuiDialogTitle>
    <Stack direction="row" spacing={4} alignItems="center">
      <Typography sx={{ fontSize: 18 }}>{title}</Typography> {subtitle && <Typography>|</Typography>}
      <Typography sx={{ fontSize: 14 }}>{subtitle}</Typography>
    </Stack>
    {!notUseCloseIcon && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
  </MuiDialogTitle>
);

interface DialogProps extends React.PropsWithChildren {
  isOpen: boolean;
  maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  sxStyle?: Record<string, string>;
}

const Dialog = ({ isOpen, maxWidth, sxStyle, children }: DialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <MuiDialog
      open={isOpen}
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
        },
        ...sxStyle,
      }}
      maxWidth={maxWidth}
    >
      {children}
    </MuiDialog>
  );
};

Dialog.Title = DialogTitle;
Dialog.Content = DialogContent;

export default Dialog;
